/**
 * The dashboard's Ship tab sections (spec 2026-10-09 §2.2 "2 · Ship", §2.1, §8 W4 card C2): Status, Loadout, Cargo
 * and Fabrication. Each renders its pure model (`model.ts`) from a `ShipTabSource` the feature builds, re-read at the
 * shell's `refreshHz` while the section is mounted (only while the dashboard is open). Docked-only actions are shown
 * disabled with the reason, the nearest known station and Set course (`DockedAction`, C0's resolvers). Arrows move
 * through the buttons, Enter presses one, F sets course for the first docked action that has a station.
 */
import { useEffect, useReducer, useRef, type ReactElement, type RefObject } from 'react'
import type { JettisonPhase } from '../../../../bridge/m2/encounters'
import type { Career } from '../../../../career/types'
import type { M2Data, StationId } from '../../../../data/m2Schemas'
import { navigateButtons } from '../../../strategic/mount'
import { toNavInput, useNavInput } from '../../../fleet/sectionHost'
import { loadDashboardConfig } from '../../config'
import { dockedActionModel, toNearestStation, type StationDirectory, type StationHit } from '../../data'
import { DockedAction } from '../../parts'
import type { DashContext, DashSection, DashSectionComponent, DashSectionProps } from '../../registry'
import { cargoModel, fabModel, jettisonModel, loadoutModel, SHIP_TAB, statusModel, type ShipDockedAction, type ShipSectionKey, type ShipVitals } from './model'
import './ship.css'

/** What the Ship tab reads and does; the `shipTab` feature builds it over the live game. */
export interface ShipTabSource {
  data(): M2Data | null
  career(): Career | null
  vitals(): ShipVitals | null
  /** The known-station directory (null outside the Reach). */
  directory(): StationDirectory | null
  /** Set course to a directory station id (C0's `setCourseTo`). */
  setCourse(stationId: string): void
  /** A Career station's display name. */
  stationName(id: StationId): string
  /** The directory station id of a Career station, or null when it is not known. */
  directoryIdOf(id: StationId): string | null
  jettisonPhase(): JettisonPhase
  jettison(share: number): void
  readonly jettisonShares: readonly number[]
  readonly jettisonAll: string
}

export const SHIP_SECTION_IDS: Readonly<Record<ShipSectionKey, string>> = Object.freeze({
  status: 'ship.status',
  loadout: 'ship.loadout',
  cargo: 'ship.cargo',
  fabrication: 'ship.fabrication',
})

const DASH = loadDashboardConfig()

/** Re-renders at the shell's refresh rate while mounted. */
function useRefresh(): void {
  const [, bump] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    const iv = window.setInterval(bump, 1000 / DASH.refreshHz)
    return (): void => window.clearInterval(iv)
  }, [])
}

interface ResolvedAction {
  readonly action: ShipDockedAction
  readonly reason: string
  readonly hit: StationHit | null
}

function resolveActions(src: ShipTabSource, key: ShipSectionKey): ResolvedAction[] {
  const dir = src.directory()
  return SHIP_TAB.dockedActions[key].map((action) => {
    const m = dir === null ? { reason: DASH.dockedAction.reasons[action.service], hit: null } : dockedActionModel(DASH.dockedAction, dir, action.service)
    return { action, reason: action.reason ?? m.reason, hit: m.hit }
  })
}

/** Arrows/Enter over the section's buttons; F sets course for the first action that has a station. */
function useShipNav(props: DashSectionProps, root: RefObject<HTMLElement | null>, onF: () => boolean): void {
  useNavInput(props.nav, (i) => {
    if (i === 'secondary') return onF()
    const n = toNavInput(i)
    const el = root.current
    return n !== null && el !== null && navigateButtons(el, n)
  })
}

function DockedActions({ src, actions }: { src: ShipTabSource; actions: readonly ResolvedAction[] }): ReactElement {
  return (
    <div className="ship-docked">
      {actions.map((a) => (
        <DockedAction
          key={a.action.label}
          cfg={DASH.dockedAction}
          label={a.action.label}
          reason={a.reason}
          nearest={toNearestStation(DASH.dockedAction, a.hit)}
          onSetCourse={() => {
            if (a.hit !== null) src.setCourse(a.hit.station.id)
          }}
        />
      ))}
    </div>
  )
}

function firstCourse(src: ShipTabSource, actions: readonly ResolvedAction[]): boolean {
  const a = actions.find((x) => x.hit !== null)
  if (a?.hit == null) return false
  src.setCourse(a.hit.station.id)
  return true
}

function Bar({ fraction, label, text, testId }: { fraction: number; label: string; text: string; testId?: string }): ReactElement {
  return (
    <div className="ship-bar" data-testid={testId}>
      <span className="ship-bar__label">{label}</span>
      <span className="ship-bar__track">
        <span className="ship-bar__fill" style={{ width: `${Math.round(fraction * 100)}%` }} />
      </span>
      <span className="ship-bar__value">{text}</span>
    </div>
  )
}

function useSection(props: DashSectionProps, src: ShipTabSource, key: ShipSectionKey): { root: RefObject<HTMLDivElement | null>; actions: ResolvedAction[] } {
  useRefresh()
  const root = useRef<HTMLDivElement | null>(null)
  const actions = resolveActions(src, key)
  useShipNav(props, root, () => firstCourse(src, actions))
  return { root, actions }
}

function StatusSection({ src, props }: { src: ShipTabSource; props: DashSectionProps }): ReactElement {
  const { root, actions } = useSection(props, src, 'status')
  const data = src.data()
  const career = src.career()
  if (data === null || career === null) return <p className="dash-empty__line">{DASH.unavailable}</p>
  const m = statusModel(data, career, src.vitals())
  const t = SHIP_TAB.status
  return (
    <div className="ship-tab" ref={root} data-testid="ship-status">
      {m.note !== null ? <p className="ship-note">{m.note}</p> : null}
      {m.bars.map((b) => (
        <Bar key={b.label} fraction={b.fraction} label={b.label} text={b.text} />
      ))}
      {m.torpedoes !== null ? (
        <p className="ship-line">
          {t.torpedoes} <b>{m.torpedoes}</b>
        </p>
      ) : null}
      <h4 className="ship-head">{t.modules}</h4>
      <table className="ship-table">
        <tbody>
          {m.modules.map((r) => (
            <tr key={r.slot}>
              <td>{r.label}</td>
              <td>{r.module}</td>
              <td className="ship-num">{r.condition}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="ship-line">
        {t.insurance}: <b>{m.insurance}</b>
      </p>
      <p className="ship-line">
        {t.flaws}: {m.flaws.length === 0 ? t.noFlaws : m.flaws.join(', ')}
      </p>
      <DockedActions src={src} actions={actions} />
    </div>
  )
}

function LoadoutSection({ src, props }: { src: ShipTabSource; props: DashSectionProps }): ReactElement {
  const { root, actions } = useSection(props, src, 'loadout')
  const data = src.data()
  const career = src.career()
  if (data === null || career === null) return <p className="dash-empty__line">{DASH.unavailable}</p>
  const m = loadoutModel(data, career)
  const t = SHIP_TAB.loadout
  return (
    <div className="ship-tab" ref={root} data-testid="ship-loadout">
      <h4 className="ship-head">
        {t.slots} <span className={m.over ? 'ship-warn' : 'ship-dim'}>{m.power}</span>
      </h4>
      <table className="ship-table">
        <tbody>
          {m.slots.map((r) => (
            <tr key={r.slot}>
              <td>{r.label}</td>
              <td>{r.module}</td>
              <td>{r.mk}</td>
              <td className="ship-num">{r.power}</td>
              <td className="ship-num">{r.condition ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4 className="ship-head">{t.locker}</h4>
      {m.locker.length === 0 ? (
        <p className="ship-dim">{t.lockerEmpty}</p>
      ) : (
        <table className="ship-table">
          <tbody>
            {m.locker.map((r) => (
              <tr key={r.key}>
                <td>{r.module}</td>
                <td>{r.mk}</td>
                <td className="ship-num">{r.count}</td>
                <td className="ship-num">{r.condition ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <DockedActions src={src} actions={actions} />
    </div>
  )
}

function CargoSection({ src, props }: { src: ShipTabSource; props: DashSectionProps }): ReactElement {
  const { root, actions } = useSection(props, src, 'cargo')
  const data = src.data()
  const career = src.career()
  if (data === null || career === null) return <p className="dash-empty__line">{DASH.unavailable}</p>
  const m = cargoModel(data, career)
  const j = jettisonModel(src.jettisonPhase(), src.jettisonShares, src.jettisonAll)
  const t = SHIP_TAB.cargo
  return (
    <div className="ship-tab" ref={root} data-testid="ship-cargo">
      <Bar fraction={m.fill} label="" text={m.capacity} testId="ship-cargo-capacity" />
      {m.empty ? (
        <p className="ship-dim">{t.empty}</p>
      ) : (
        <table className="ship-table" data-testid="ship-cargo-manifest">
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.key} data-cargo-kind={r.kind}>
                <td>{r.name}</td>
                <td>{r.mk ?? ''}</td>
                <td className="ship-num">{r.amount}</td>
                <td className="ship-num">{r.condition ?? ''}</td>
                <td>
                  {r.flags.map((f) => (
                    <span key={f} className="ship-flag">
                      {f}
                    </span>
                  ))}
                </td>
                <td className="ship-num">
                  {r.value}
                  {r.base ? <span className="ship-dim"> ({t.basePrice})</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="ship-line" data-testid="ship-cargo-value">
        <b>{m.totalText}</b> <span className="ship-dim">{m.priceAge}</span>
      </p>
      <div className="ship-jettison" data-testid="ship-jettison">
        <span className="ship-head">{t.jettison}</span>
        {j.shares.map((s) => (
          <button key={s.share} type="button" className="ship-btn" disabled={!j.enabled} data-testid="ship-jettison-share" onClick={() => src.jettison(s.share)}>
            {s.label}
          </button>
        ))}
        <p className="ship-dim">{j.reason ?? t.jettisonHint}</p>
      </div>
      <DockedActions src={src} actions={actions} />
    </div>
  )
}

function FabricationSection({ src, props }: { src: ShipTabSource; props: DashSectionProps }): ReactElement {
  const { root, actions } = useSection(props, src, 'fabrication')
  const data = src.data()
  const career = src.career()
  if (data === null || career === null) return <p className="dash-empty__line">{DASH.unavailable}</p>
  const m = fabModel(data, career, (id) => src.stationName(id))
  const t = SHIP_TAB.fabrication
  return (
    <div className="ship-tab" ref={root} data-testid="ship-fabrication">
      {m.orders.length === 0 ? <p className="ship-dim">{t.empty}</p> : null}
      {m.orders.map((o) => {
        const dirId = src.directoryIdOf(o.stationId)
        return (
          <div key={o.id} className="ship-fab">
            <h4 className="ship-head">{o.module}</h4>
            <Bar fraction={o.fraction} label="" text={o.progress} />
            {o.carry !== null ? <p className="ship-line">{o.carry}</p> : null}
            <p className="ship-line">
              {o.ready ? <b>{t.ready} · </b> : null}
              {o.collect}{' '}
              {dirId !== null ? (
                <button type="button" className="ship-btn" onClick={() => src.setCourse(dirId)}>
                  {t.setCourse}
                </button>
              ) : null}
            </p>
          </div>
        )
      })}
      <DockedActions src={src} actions={actions} />
    </div>
  )
}

const BODIES: Readonly<Record<ShipSectionKey, (p: { src: ShipTabSource; props: DashSectionProps }) => ReactElement>> = {
  status: StatusSection,
  loadout: LoadoutSection,
  cargo: CargoSection,
  fabrication: FabricationSection,
}

/** The four Ship sections over `src` (a career in the Reach; elsewhere the shell shows their empty state). */
export function shipSections(src: ShipTabSource): DashSection[] {
  return (Object.keys(SHIP_SECTION_IDS) as ShipSectionKey[]).map((key): DashSection => {
    const Body = BODIES[key]
    const component: DashSectionComponent = (props) => <Body src={src} props={props} />
    return {
      id: SHIP_SECTION_IDS[key],
      tab: 'ship',
      order: SHIP_TAB.sections[key].order,
      label: SHIP_TAB.sections[key].label,
      available: (ctx: DashContext) => ctx.career,
      component,
    }
  })
}
